import { Link } from 'react-router-dom'

import type { EagleCausa, EagleSondaVerdetto } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * The root-cause verdict Eagle attaches to a reachability problem, in one
 * short line: behind which object it sits (collateral), how many it drags
 * down (root), from which probe it cannot be reached (path), or whether a
 * blind probe is to blame. Short on purpose: it goes next to a problem row or
 * on a wall tile.
 */
export function testoCausa(c: EagleCausa): string {
  switch (c.kind) {
    case 'collateral':
      return t('dietro {root}', { root: c.root_name ?? '—' })
    case 'probe':
      return t('colpa della sonda {probe}', { probe: c.probe })
    case 'path':
      return c.downstream > 0
        ? t('non raggiunto da {probes}, {n} dietro', { probes: c.probes.join(', '), n: c.downstream })
        : t('non raggiunto da {probes}', { probes: c.probes.join(', ') })
    case 'root':
      return t('trascina {n} oggetti', { n: c.downstream })
  }
}

/** The verdict on a probe (blind or silent), as the dashboard and the probes page show it. */
export function testoVerdetto(v: EagleSondaVerdetto): string {
  return v.kind === 'cieca'
    ? t('cieca: {down} su {total} non raggiunti', { down: v.down, total: v.total })
    : t('ferma: {n} oggetti senza letture', { n: v.down })
}

/**
 * The root-cause chip next to a problem: «↳ dietro SW-CORE» (a link to the
 * root, unless the chip already sits inside a link), «⚑ trascina 12 oggetti»,
 * «⚑ non raggiunto da roma, 12 dietro» (the probes that do see it in the
 * tooltip), «↳ colpa della sonda roma».
 */
export function Causa({ c, link = true }: { c: EagleCausa | null | undefined; link?: boolean }) {
  if (!c) return null
  const dietro = c.kind === 'collateral' || c.kind === 'probe'
  const classe = `ecausa ${dietro ? 'ecausa--dietro' : 'ecausa--radice'}`
  const titolo = c.kind === 'path' && c.seen_by.length > 0
    ? t('raggiungibile da {probes}', { probes: c.seen_by.join(', ') })
    : c.kind === 'probe' ? t('La sonda non raggiunge più quasi nulla di ciò che legge: la causa è lei, non l’oggetto.') : undefined
  const testo = `${dietro ? '↳' : '⚑'} ${testoCausa(c)}`
  if (c.kind === 'collateral' && c.root_id && link) {
    return <Link to={`/cis/${c.root_id}#eagle`} className={classe} title={titolo}>{testo}</Link>
  }
  return <span className={classe} title={titolo}>{testo}</span>
}

/** The chip for a probe that is itself the cause (blind or silent). */
export function VerdettoSonda({ v, conNome = false }: { v: EagleSondaVerdetto | null | undefined; conNome?: boolean }) {
  if (!v) return null
  const titolo = v.objects.length > 0 ? v.objects.join(', ') + (v.down > v.objects.length ? ` … (+${v.down - v.objects.length})` : '') : undefined
  return <span className="ecausa ecausa--sonda" title={titolo}>{conNome ? `${t('sonda {name}', { name: v.name })} · ` : ''}{testoVerdetto(v)}</span>
}
